/* eslint-disable @typescript-eslint/unbound-method */
import FeedbackData, {
  FeedbackType,
} from '@enview/interface/types/actions/FeedbackData';
import { faThumbsDown, faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UserActionAPI } from '../../../api';
import { State } from '../../../dux/@types';
import './FeedbackPrompt.scss';

type SubmitTextFeedbackFormData = {
  [key: string]: string;
};

export type FeedbackPromptProps = {
  feedbackType: FeedbackType;
  message?: ReactElement;
  setFeedbackState: (isPositive: boolean) => void;
  submitFeedbackMetadata?: unknown;
};

const FeedbackPrompt = (props: FeedbackPromptProps): ReactElement => {
  const { feedbackType, message, setFeedbackState, submitFeedbackMetadata } = props;
  const [isPositiveState, setIsPositiveState] = useState<boolean | undefined>(
    undefined,
  );
  const [isMessageSubmitted, setIsMessageSubmitted] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const { t } = useTranslation();
  const { errors, register, handleSubmit } = useForm();

  const getType = (): string => {
    switch (feedbackType) {
      case FeedbackType.BillSummaryInaccurate:
      case FeedbackType.VersionToVersionSummaryInaccurate:
        return 'summary';
      default:
        return 'data';
    }
  };

  const dataFieldName = `feedback-text-area-${getType()}`;

  const [sendTextFeedback, { isError }] =
    UserActionAPI.endpoints.sendFeedback.useMutation();
  const orgUser = useSelector((state: State) => state.account.organizationUser);

  const getMessage = (): ReactElement => {
    return (
      message || (
        <span className="feedback-header-text">
          {t('feedback.feedbackHeaderText', { feedbackType: getType() })}
        </span>
      )
    );
  };

  const onClick = (isYesClick: boolean): void => {
    setIsPositiveState(isYesClick);
    setFeedbackState(isYesClick);
  };

  const onSubmitTextFeedback = async (
    data: SubmitTextFeedbackFormData,
  ): Promise<void> => {
    const feedbackData: FeedbackData = {
      feedbackType,
      organizationUserId: orgUser?.id ?? 0,
      username: orgUser?.email ?? '',
      message: data[dataFieldName],
      metadata: submitFeedbackMetadata,
    };

    await sendTextFeedback(feedbackData).then(() => {
      if (isError) {
        setShowErrorMessage(true);
        setTimeout(() => setShowErrorMessage(false), 500);
      } else {
        setIsMessageSubmitted(true);
      }
    });
  };

  return (
    <div className="feedback-prompt">
      {getMessage()}
      <div className="feedback-button-group">
        <Button
          aria-label={t('buttons.yesText')}
          className={`btn feedback-prompt-button positive-state ${
            isPositiveState === true ? 'selected' : ''
          }`}
          disabled={isPositiveState !== undefined}
          onClick={() => onClick(true)}
          type="button"
        >
          <FontAwesomeIcon icon={faThumbsUp} />
        </Button>
        <Button
          aria-label={t('buttons.noText')}
          className={`btn feedback-prompt-button negative-state ${
            isPositiveState === false ? 'selected' : ''
          }`}
          disabled={isPositiveState !== undefined}
          onClick={() => onClick(false)}
          type="button"
        >
          <FontAwesomeIcon icon={faThumbsDown} />
        </Button>
      </div>

      {isPositiveState === true && <span> {t('feedback.feedbackSuccess')}</span>}
      {isPositiveState === false && (
        <>
          {!isMessageSubmitted && (
            <form onSubmit={handleSubmit(onSubmitTextFeedback)}>
              <textarea
                className="form-control form-field feedback-text-area"
                id={dataFieldName}
                name={dataFieldName}
                placeholder={t('feedback.feedbackPromptNegativePlaceholder', {
                  feedbackType: getType(),
                })}
                ref={register({ required: true })}
              />
              <Button
                className="btn btn-primary"
                disabled={isMessageSubmitted}
                type="submit"
              >
                {t('feedback.submitFeedback')}
              </Button>
              {errors[dataFieldName] && (
                <div className="error alert alert-danger">
                  {t('validation.missingField')}
                </div>
              )}
            </form>
          )}
          <div
            className={`submit-error-container ${
              isMessageSubmitted ? 'submitted' : ''
            }`}
          >
            {showErrorMessage && (
              <div className="error alert alert-danger">
                {t('error.genericErrorOccurred')}
              </div>
            )}
            {isMessageSubmitted && <span>{t('feedback.feedbackSuccess')}</span>}
          </div>
        </>
      )}
    </div>
  );
};

export default FeedbackPrompt;
